// composables/useOffersRevalidate.ts
import { onMounted, onBeforeUnmount } from 'vue';
import { useOffersStore } from '~/store/offers';

/**
 * Периодическая ревалидация списка офферов (без блокировки UI).
 *
 * Как и раньше:
 *  - раз в N мс тянет /offers;
 *  - сравнивает resp.version и checksum по resp.items;
 *  - при отличиях обновляет стор setFromResp(resp).
 *
 * Дополнительно:
 *  - после обновления снимка догружаем активные департаменты (departmentIds).
 */
export function useOffersRevalidate(intervalMs = 10 * 60 * 1000) {
    const { $OffersApi } = useNuxtApp();
    const store = useOffersStore();

    let timer: number | undefined;
    let inFlight = false;

    /** Локальный fnv1a чек-сум по массиву OfferInfo — используем из стора. */
    function checksumOfResp(resp: any): string {
        const items = Array.isArray(resp?.items) ? resp.items : [];
        return store._checksum(items);
    }

    async function revalidate() {
        if (inFlight) return;
        inFlight = true;
        try {
            // Тянем снимок
            const resp = await $OffersApi.getOffers({});
            const items = Array.isArray(resp?.items) ? resp.items : [];

            // 1) Сравнение версий
            const sameVersion = !!resp?.version && resp.version === store.version;

            // 2) Сравнение checksum (на случай локального рассинхрона)
            const newChecksum = store._checksum(items);
            const sameChecksum = newChecksum === store.checksum;

            // Если оба совпадают — обновлять нечего
            if (sameVersion && sameChecksum) return;

            // Обновляем стор в один проход
            if (typeof store.setFromResp === 'function') {
                store.setFromResp(resp);
            } else {
                // Фоллбек: если кто-то ещё не добавил setFromResp
                store.setOffers(items, resp?.version);
            }

            // Подтянуть активные департаменты (версию внутри не используем для логики)
            try {
                await store.fetchDepartmentIdsIfPossible?.();
            } catch { /* ignore optional enrichment errors */ }
        } catch {
            // сетевые/серверные ошибки тихо игнорируем — повторим на следующем тике/фокусе
        } finally {
            inFlight = false;
        }
    }

    onMounted(() => {
        // Периодическая ревалидация
        timer = window.setInterval(revalidate, Math.max(15_000, intervalMs));

        // Ревалидация при возврате во вкладку
        const onVis = () => {
            if (document.visibilityState === 'visible') revalidate();
        };
        document.addEventListener('visibilitychange', onVis);

        onBeforeUnmount(() => {
            if (timer) clearInterval(timer);
            document.removeEventListener('visibilitychange', onVis);
        });
    });

    return { revalidate };
}
